import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { NavLink, useNavigate, useParams, useLocation } from 'react-router-dom';
import { IoIosAdd, IoMdTrash } from 'react-icons/io';
import DebouncedInput from '../../../components/UI/DebouncedInput/DebouncedInput';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { therapyActions } from '../../../store/slices/therapy';
import { Therapy } from '../../../interfaces/therapy';
import { TherapyResponse } from 'services/TherapyService';
import { PaginationState } from '@tanstack/react-table';
import ListPagination from 'components/UI/ListPagination/ListPagination';
import ReactTooltip from 'react-tooltip';

/**
 * Props for the TherapyList component
 * @typedef {Object} TherapyListProps
 * @property {TherapyResponse | undefined} therapyResponse - Response containing therapy records and pagination data
 * @property {Function} updateFilter - Function to update the filter for therapy list
 * @property {Function} onDelete - Function to handle therapy deletion
 * @property {boolean} canManage - Whether the user has permission to manage therapies
 * @property {Dispatch<SetStateAction<PaginationState>>} setPagination - Function to update pagination state
 * @property {PaginationState} pagination - Current pagination state
 * @property {boolean} refetch - Flag indicating whether data should be refetched
 */
type TherapyListProps = {
  therapyResponse: TherapyResponse | undefined;
  updateFilter: Function;
  onDelete: Function;
  canManage: boolean;
  setPagination: Dispatch<SetStateAction<PaginationState>>;
  pagination: PaginationState;
  refetch: boolean;
};

/**
 * @description Component for displaying and managing a list of therapies with search, pagination, and delete capabilities.
 *
 * @SADtrace SAD-003-3.5
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {TherapyListProps} props - Component props
 *
 * @returns {JSX.Element} Rendered component
 */
const TherapyList: FC<TherapyListProps> = ({
  therapyResponse,
  updateFilter,
  onDelete,
  canManage,
  setPagination,
  pagination,
  refetch,
}) => {
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const clearSearch: boolean = useSelector(
    (state: any) => state.therapy.clearSearch,
  );
  const dispatch = useDispatch();
  const [search, setSearch] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    const isAdding = location.pathname.includes('/add');
    if (
      !params.id &&
      therapyResponse?.records.length &&
      !refetch &&
      !isAdding
    ) {
      navigate(`/therapies/${therapyResponse.records[0].id}`);
    }
  }, [therapyResponse, params.id, refetch, location.pathname, navigate]);

  /**
   * @description Handles the add therapy button click.
   * Validates if a therapy with the same name already exists.
   * Navigates to the add therapy page with the search term as a query parameter.
   *
   * @SADtrace SAD-003-3.5
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @returns {boolean|undefined} Returns false if therapy with same name exists
   */
  const handleAddClick = () => {
    if (!therapyResponse?.records) return;

    const therapyExists = therapyResponse?.records.find(
      (therapy: Therapy) =>
        therapy.name.toLowerCase().trim() === search.toLowerCase().trim(),
    );

    if (therapyExists) {
      toast.error(
        t('therapies.messages.name__exists', { name: therapyExists.name }),
      );
      return false;
    }

    navigate({
      pathname: '/therapies/add',
      search: `?name=${search}`,
    });
  };

  useEffect(() => {
    if (clearSearch) {
      setSearch('');
      updateFilter('');
      dispatch(therapyActions.resetClearSearch());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSearch]);

  return (
    <>
      <div className="master-list__search-wrapper">
        <DebouncedInput
          placeholder={t('therapies.search_box')}
          maxLength={20}
          value={search}
          type="text"
          onChange={(value) => {
            setSearch(value as string);
            updateFilter(value);

            //set pagination back to first page
            setPagination((oldValue) => ({
              ...oldValue,
              pageIndex: 0,
            }));
          }}
          className="form-control form-control rounded mb-2"
          data-testid="search_therapy_list_input"
        />

        {canManage ? (
          <>
            <button
              className="btn-rounded btn-rounded-sm master-list__button-add"
              onClick={handleAddClick}
              data-testid="add"
              data-tip
              data-for="therapies-add-tooltip"
            >
              <IoIosAdd size={40} />
            </button>
            <ReactTooltip id="therapies-add-tooltip" effect="solid" place="top">
              Add Therapy
            </ReactTooltip>
          </>
        ) : null}
      </div>

      {therapyResponse?.records.length === 0 && (
        <div className="alert mb-0">{t('messages.no_records')}</div>
      )}

      {therapyResponse && therapyResponse?.records.length > 0 && (
        <div className="master-list master-list__no-scroll">
          {therapyResponse.records.map((therapy: Therapy) => {
            return (
              <NavLink
                to={`/therapies/${therapy.id}`}
                className="master-list__item"
                key={therapy.id}
              >
                {therapy.name}

                {canManage && (
                  <>
                    <button
                      className="master-list__item-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onDelete(therapy);
                      }}
                      data-testid="delete"
                      data-tip
                      data-for="therapies-delete-tooltip"
                    >
                      <IoMdTrash size={26} />
                    </button>
                    <ReactTooltip
                      id="therapies-delete-tooltip"
                      effect="solid"
                      place="top"
                    >
                      Delete Therapy
                    </ReactTooltip>
                  </>
                )}
              </NavLink>
            );
          })}
          <ListPagination
            canGetNextPage={
              Number(therapyResponse.pageIndex + 1) <
              Number(therapyResponse.totalPages)
            }
            canGetPreviousPage={Number(therapyResponse.pageIndex) > 0}
            nextPage={() => {
              setPagination({
                ...pagination,
                pageIndex: pagination.pageIndex + 1,
              });
            }}
            previousPage={() => {
              setPagination({
                ...pagination,
                pageIndex: pagination.pageIndex - 1,
              });
            }}
            pageCount={therapyResponse.totalPages}
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            // v8 ignore start -- WAIVER(DEFENSIVE): handler required by the ListPagination prop contract; the rows-per-page selector is hidden product-wide via hidePageSize, so it can never fire
            setPageSize={(pageSize: number) =>
              setPagination({
                ...pagination,
                pageSize,
              })
            }
            // v8 ignore stop -- WAIVER(DEFENSIVE)
            setPageIndex={(pageIndex: number) =>
              setPagination({
                ...pagination,
                pageIndex,
              })
            }
            hidePageSize
          />
        </div>
      )}
    </>
  );
};

export default TherapyList;
